<template>
  <div class="akademie-container py-4 py-md-6 px-3 px-md-6">
    <div class="akademie-wrap">
      <!-- Back Bar -->
      <div class="d-flex align-center mb-3">
        <v-btn icon dark @click="goBack" class="mr-2">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="grey--text text-caption">Zurück zum Dashboard</span>
      </div>

    <!-- Hero -->
    <v-card dark class="hero-card rounded-xl mb-6 mb-md-8" elevation="0">
      <div class="hero-glow glow-a"></div>
      <div class="hero-glow glow-b"></div>

      <div class="hero-inner pa-5 pa-md-8 d-flex flex-column flex-md-row align-md-center justify-space-between">
        <div class="hero-text">
          <div class="hero-eyebrow text-caption font-weight-bold mb-2 d-flex align-center">
            <v-icon size="14" color="#D4AF37" class="mr-1">mdi-decagram</v-icon>
            <span class="gold-text-plain">WELLCOMEFIT AKADEMIE</span>
          </div>
          <h1 class="hero-title font-weight-bold mb-2">
            Willkommen, <span class="gold-text">{{ userFirstName }}!</span>
          </h1>
          <p class="hero-subtitle grey--text text--lighten-1 mb-4">
            Lerne in deinem Tempo · sammle Lizenzen · werde Teil der Community.
          </p>

          <div class="d-flex flex-wrap gap-2">
            <v-btn v-if="isTeacher" outlined color="#D4AF37" rounded class="text-none" to="/akademie-teacher">
              <v-icon left small>mdi-account-tie</v-icon> Teacher
            </v-btn>
            <v-btn v-if="isAdmin" outlined color="#D4AF37" rounded class="text-none" to="/akademie-admin">
              <v-icon left small>mdi-shield-crown</v-icon> Admin
            </v-btn>
          </div>
        </div>

        <!-- License Badges -->
        <div class="badges-wrap mt-5 mt-md-0">
          <div v-for="(tier, idx) in ['bronze', 'silber', 'gold']" :key="tier" class="badge-col" :style="{ animationDelay: idx * 0.12 + 's' }">
            <div class="license-coin" :class="{ active: hasLicense(tier) }">
              <v-icon size="34" :color="hasLicense(tier) ? getTierColor(tier) : '#3a3a3a'">mdi-medal</v-icon>
              <div class="coin-glow" v-if="hasLicense(tier)"></div>
            </div>
            <div class="coin-label" :class="{ active: hasLicense(tier) }">{{ tierLabel(tier) }}</div>
          </div>
        </div>
      </div>

      <!-- Meine angemeldeten kommenden Schulungen -->
      <div v-if="myUpcoming.length" class="my-trainings-strip">
        <div class="d-flex align-center px-4 px-md-6 pt-3">
          <v-icon size="18" color="#D4AF37" class="mr-2">mdi-calendar-clock</v-icon>
          <span class="text-caption gold-text-plain font-weight-bold" style="letter-spacing: 1.5px; text-transform: uppercase">
            Deine nächsten Schulungen
          </span>
        </div>
        <div class="my-trainings-scroll px-4 px-md-6 pb-3 pt-2">
          <div
            v-for="t in myUpcoming"
            :key="t._id"
            class="training-pill"
            :class="t.myRegistration && t.myRegistration.status === 'waiting_list' ? 'wait' : ''"
            @click="$router.push('/akademie/schulungen/' + t._id)">
            <div class="pill-date">
              <div class="pill-day">{{ day(t.startsAt) }}</div>
              <div class="pill-month">{{ monthShort(t.startsAt) }}</div>
            </div>
            <div class="pill-body">
              <div class="pill-title text-truncate">{{ t.title }}</div>
              <div class="pill-meta text-truncate">
                <v-icon x-small color="grey">mdi-clock-outline</v-icon>
                {{ time(t.startsAt) }}
                <template v-if="t.location"> · {{ t.location }}</template>
              </div>
              <div v-if="t.myRegistration && t.myRegistration.status === 'waiting_list'" class="pill-status wait">Warteliste</div>
              <div v-else class="pill-status ok">Angemeldet</div>
            </div>
            <v-icon size="18" color="grey">mdi-chevron-right</v-icon>
          </div>
        </div>
      </div>

      <!-- Stats Strip -->
      <div class="stats-strip d-flex flex-wrap">
        <div class="stat-item">
          <div class="stat-value">{{ activeLicenseCount }}</div>
          <div class="stat-label">aktive Lizenzen</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <div class="stat-value gold-text">{{ openTermineCount }}</div>
          <div class="stat-label">offene Schulungen</div>
        </div>
        <div class="stat-divider"></div>
        <div class="stat-item">
          <div class="stat-value">{{ completedModuleCount }}</div>
          <div class="stat-label">abgeschl. Module</div>
        </div>
      </div>
    </v-card>

    <!-- Main Tabs (Desktop) -->
    <v-tabs
      v-if="!isMobile"
      centered
      dark
      v-model="activeTab"
      background-color="transparent"
      color="#D4AF37"
      slider-color="#D4AF37"
      class="mb-6 modern-tabs"
      show-arrows>
      <v-tab to="/akademie/suite"><v-icon left small>mdi-book-open-page-variant</v-icon> Learning Suite</v-tab>
      <v-tab to="/akademie/kreativwerkstatt"><v-icon left small>mdi-lightbulb-on-outline</v-icon> Kreativwerkstatt</v-tab>
      <v-tab to="/akademie/schulungen">
        <v-icon left small>mdi-calendar-star</v-icon> Schulungsplan
        <v-chip v-if="openTermineCount > 0" x-small color="#D4AF37" text-color="black" class="ml-2 font-weight-bold tab-chip">
          {{ openTermineCount }}
        </v-chip>
      </v-tab>
    </v-tabs>

    <!-- Mobile Nav: vertikale Pillen statt enge Tabs -->
    <div v-else class="mobile-nav mb-5">
      <button
        v-for="nav in mobileNav"
        :key="nav.path"
        class="mobile-nav-btn"
        :class="{ active: isActiveRoute(nav.path) }"
        @click="$router.push(nav.path)">
        <v-icon :color="isActiveRoute(nav.path) ? '#D4AF37' : 'grey lighten-1'">{{ nav.icon }}</v-icon>
        <span class="mobile-nav-label">{{ nav.label }}</span>
        <v-chip v-if="nav.path === '/akademie/schulungen' && openTermineCount > 0" x-small color="#D4AF37" text-color="black" class="ml-auto font-weight-bold tab-chip">
          {{ openTermineCount }}
        </v-chip>
        <v-icon v-else size="18" :color="isActiveRoute(nav.path) ? '#D4AF37' : 'grey'" class="ml-auto">mdi-chevron-right</v-icon>
      </button>
    </div>

    <v-fade-transition mode="out-in">
      <router-view></router-view>
    </v-fade-transition>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"

export default {
  name: "AkademieMain",
  data() {
    return {
      userService,
      activeTab: 0,
      myLicenses: [],
      termine: [],
      progresses: [],
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
      mobileNav: [
        { path: "/akademie/suite", icon: "mdi-book-open-page-variant", label: "Learning Suite" },
        { path: "/akademie/kreativwerkstatt", icon: "mdi-lightbulb-on-outline", label: "Kreativwerkstatt" },
        { path: "/akademie/schulungen", icon: "mdi-calendar-star", label: "Schulungsplan" },
      ],
    }
  },
  computed: {
    isMobile() {
      return this.windowWidth < 600
    },
    userFirstName() {
      return this.userService.getUserFirstName()
    },
    isTeacher() {
      return this.userService.hasRight("aka_teacher") || this.userService.hasRight("aka_manage")
    },
    isAdmin() {
      return this.userService.hasRight("aka_manage")
    },
    activeLicenseCount() {
      return (this.myLicenses || []).filter((l) => l.status === "active" || l.status === "expiring_soon").length
    },
    openTermineCount() {
      const now = new Date()
      return (this.termine || []).filter((t) => {
        if (!t.registrationEnd) return false
        return new Date(t.registrationEnd) >= now && new Date(t.startsAt) >= now
      }).length
    },
    completedModuleCount() {
      return (this.progresses || []).filter((p) => p.completedAt).length
    },
    myUpcoming() {
      const now = new Date()
      return (this.termine || [])
        .filter(
          (t) =>
            t.myRegistration &&
            ["registered", "waiting_list"].includes(t.myRegistration.status) &&
            t.startsAt &&
            new Date(t.startsAt) >= now,
        )
        .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt))
        .slice(0, 6)
    },
  },
  mounted() {
    this.loadAll()
    this.onResize = () => (this.windowWidth = window.innerWidth)
    window.addEventListener("resize", this.onResize)
  },
  beforeDestroy() {
    if (this.onResize) window.removeEventListener("resize", this.onResize)
  },
  methods: {
    isActiveRoute(path) {
      return this.$route && this.$route.path.startsWith(path)
    },
    goBack() {
      this.$router.push("/dashboard")
    },
    day(d) {
      return d ? new Date(d).getDate() : ""
    },
    monthShort(d) {
      return d ? new Date(d).toLocaleDateString("de-CH", { month: "short" }).replace(".", "").toUpperCase() : ""
    },
    time(d) {
      return d ? new Date(d).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" }) : ""
    },
    async loadAll() {
      try {
        const [lic, ter, prog] = await Promise.all([
          api.akademie.fetchMyLicenses().catch(() => []),
          api.akademie.fetchTermine().catch(() => []),
          api.akademie.fetchProgress().catch(() => []),
        ])
        this.myLicenses = lic || []
        this.termine = ter || []
        this.progresses = prog || []
      } catch (err) {
        console.error("Akademie Daten konnten nicht geladen werden", err)
      }
    },
    hasLicense(tier) {
      return this.myLicenses.some((l) => l.tier === tier && (l.status === "active" || l.status === "expiring_soon"))
    },
    getTierColor(tier) {
      const colors = { bronze: "#CD7F32", silber: "#C0C0C0", gold: "#FFD700" }
      return colors[tier] || "grey"
    },
    tierLabel(tier) {
      const map = { bronze: "Bronze", silber: "Silber", gold: "Gold" }
      return map[tier] || tier
    },
  },
}
</script>

<style scoped>
.akademie-container {
  background-color: #0d0d0d;
  min-height: 100vh;
}
.akademie-wrap {
  max-width: 1280px;
  margin: 0 auto;
}

.gold-text {
  background: linear-gradient(45deg, #d4af37, #ffd700);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.gold-text-plain { color: #d4af37; letter-spacing: 2px; }

.gap-2 { gap: 8px; }

/* ===== HERO ===== */
.hero-card {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(35, 30, 20, 0.9) 0%, rgba(20, 20, 20, 0.95) 60%, rgba(15, 15, 15, 0.98) 100%) !important;
  border: 1px solid rgba(212, 175, 55, 0.15);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(212, 175, 55, 0.06);
}

.hero-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
  z-index: 0;
}
.glow-a {
  width: 360px; height: 360px;
  top: -120px; left: -80px;
  background: rgba(212, 175, 55, 0.18);
  animation: floatA 12s ease-in-out infinite;
}
.glow-b {
  width: 280px; height: 280px;
  bottom: -120px; right: -60px;
  background: rgba(255, 215, 0, 0.12);
  animation: floatB 14s ease-in-out infinite;
}

@keyframes floatA {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(40px, 30px); }
}
@keyframes floatB {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-30px, -40px); }
}

.hero-inner {
  position: relative;
  z-index: 1;
}

.hero-eyebrow {
  color: #d4af37;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.hero-title {
  color: white;
  line-height: 1.1;
  font-size: 2rem;
  animation: slideIn 0.7s ease both;
}
@media (min-width: 600px) { .hero-title { font-size: 2.4rem; } }
@media (min-width: 960px) { .hero-title { font-size: 2.8rem; } }

.hero-subtitle {
  max-width: 540px;
  font-size: 0.95rem;
  line-height: 1.5;
  animation: slideIn 0.9s ease both;
}
.hero-text {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 720px;
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== BADGES ===== */
.badges-wrap {
  display: flex;
  gap: 14px;
  align-items: flex-end;
  flex-shrink: 0;
}
.badge-col {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  animation: pop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}
@keyframes pop {
  0% { opacity: 0; transform: scale(0.5) translateY(20px); }
  100% { opacity: 1; transform: scale(1) translateY(0); }
}

.license-coin {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(30, 30, 30, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 2px solid #2a2a2a;
  transition: all 0.4s ease;
}
@media (min-width: 600px) {
  .license-coin { width: 72px; height: 72px; }
}

.license-coin.active {
  border-color: #d4af37;
  box-shadow: 0 0 24px rgba(212, 175, 55, 0.35);
}
.license-coin.active::before {
  content: "";
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 1px solid rgba(212, 175, 55, 0.3);
  animation: ringPulse 2.4s ease-in-out infinite;
}
@keyframes ringPulse {
  0%, 100% { transform: scale(1); opacity: 0.6; }
  50% { transform: scale(1.12); opacity: 0; }
}

.coin-glow {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  box-shadow: 0 0 20px rgba(212, 175, 55, 0.4);
  z-index: -1;
}

.coin-label {
  font-size: 0.7rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.4);
  font-weight: 600;
}
.coin-label.active { color: #d4af37; }

/* ===== MEINE SCHULUNGEN ===== */
.my-trainings-strip {
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.18);
  backdrop-filter: blur(4px);
}
.my-trainings-scroll {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scrollbar-width: thin;
}
.my-trainings-scroll::-webkit-scrollbar {
  height: 6px;
}
.my-trainings-scroll::-webkit-scrollbar-thumb {
  background: rgba(212, 175, 55, 0.3);
  border-radius: 3px;
}

.training-pill {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-width: 260px;
  max-width: 320px;
  background: rgba(30, 30, 30, 0.85);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 12px;
  padding: 8px 12px;
  cursor: pointer;
  transition: transform 0.2s, border-color 0.2s, box-shadow 0.2s;
}
.training-pill:hover {
  transform: translateY(-2px);
  border-color: #d4af37;
  box-shadow: 0 6px 14px rgba(212, 175, 55, 0.12);
}
.training-pill.wait {
  border-color: rgba(255, 167, 38, 0.35);
}

.pill-date {
  text-align: center;
  width: 44px;
  flex-shrink: 0;
  background: rgba(212, 175, 55, 0.12);
  border-radius: 8px;
  padding: 4px 0;
}
.pill-day {
  font-size: 1.1rem;
  font-weight: 800;
  color: white;
  line-height: 1.1;
}
.pill-month {
  font-size: 0.6rem;
  color: #d4af37;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.pill-body {
  flex: 1;
  min-width: 0;
}
.pill-title {
  font-size: 0.9rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.pill-meta {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
}
.pill-status {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 99px;
  margin-top: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pill-status.ok {
  background: rgba(76, 175, 80, 0.15);
  color: #66bb6a;
}
.pill-status.wait {
  background: rgba(255, 167, 38, 0.15);
  color: #ffb74d;
}

/* ===== STATS STRIP ===== */
.stats-strip {
  position: relative;
  z-index: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(6px);
}
.stat-item {
  flex: 1 1 0;
  padding: 14px 12px;
  text-align: center;
  min-width: 120px;
}
.stat-value {
  font-size: 1.4rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
}
.stat-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 2px;
}
.stat-divider {
  width: 1px;
  background: rgba(255, 255, 255, 0.05);
}

@media (max-width: 600px) {
  .stat-divider { display: none; }
  .stat-item { flex: 1 1 33%; padding: 10px 6px; }
  .stat-value { font-size: 1.1rem; }
}

/* ===== TABS ===== */
.modern-tabs :deep(.v-tab) {
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: none;
  font-size: 0.95rem;
}
.tab-chip {
  animation: chipPulse 2s ease-in-out infinite;
}
@keyframes chipPulse {
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.5); }
  50% { transform: scale(1.05); box-shadow: 0 0 0 6px rgba(212, 175, 55, 0); }
}

@media (max-width: 600px) {
  .akademie-container { padding-left: 8px; padding-right: 8px; }
  .badges-wrap { gap: 10px; justify-content: flex-start; }
  .license-coin { width: 56px; height: 56px; }
}

/* ===== MOBILE NAV ===== */
.mobile-nav {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.mobile-nav-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: rgba(30, 30, 30, 0.85);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 14px;
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  text-align: left;
  width: 100%;
  cursor: pointer;
  transition: all 0.25s ease;
  outline: none;
}
.mobile-nav-btn:hover {
  border-color: rgba(212, 175, 55, 0.3);
  transform: translateY(-1px);
}
.mobile-nav-btn.active {
  border-color: #d4af37;
  background: linear-gradient(135deg, rgba(212, 175, 55, 0.12), rgba(30, 30, 30, 0.85));
  box-shadow: 0 4px 16px rgba(212, 175, 55, 0.18);
}
.mobile-nav-btn.active .mobile-nav-label {
  color: #d4af37;
}
.mobile-nav-label {
  flex: 1;
}
</style>
